import React from 'react';
import { Upload, Wand2, CheckCircle, Truck, MessageCircle,
         RefreshCw, HelpCircle, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Layout from '../components/layout/Layout';
import { HOW_IT_WORKS_STEPS, FAQS, BRAND } from '../data/mock';

const iconMap = { Upload, Wand2, CheckCircle, Truck };

const detailedSteps = [
  {
    icon: Upload,
    title: "Send Us Your Photo",
    description: "Share your favorite photo via Instagram DM or WhatsApp. We recommend high-resolution, well-lit, front-facing photos for best results.",
    tips: [
      "High-resolution, clear photos work best",
      "Front-facing shots for portraits",
      "Good lighting (natural light preferred)",
      "Simple backgrounds help focus on the subject"
    ],
    image: "/images/how-step-1.png",
    accent: "#F43F5E"
  },
  {
    icon: MessageCircle,
    title: "Tell Us What You Want",
    description: "Share your vision with us. Let us know the product, size, colour and any style preferences you have in mind.",
    tips: [
      "Which product you want (T-shirt / Hoodie)",
      "Preferred size and color",
      "Any specific style preferences",
      "Your delivery address"
    ],
    image: "/images/how-step-2.png",
    accent: "#8B5CF6"
  },
  {
    icon: Wand2,
    title: "AI Magic Happens",
    description: "Our advanced AI transforms your photo into stunning anime-style artwork within 24 hours. Every artwork is unique to you.",
    tips: [
      "Processing takes about 24 hours",
      "Multiple style options available",
      "High-quality output for crisp prints",
      "Each artwork is unique to you"
    ],
    image: "/images/how-step-3.png",
    accent: "#3B82F6"
  },
  {
    icon: CheckCircle,
    title: "Preview & Approve",
    description: "We share a digital preview of your artwork before printing. You get up to 2 free revisions to make sure you love it.",
    tips: [
      "Review your custom design",
      "Request up to 2 free revisions",
      "Approve when you're happy",
      "Payment confirmation after approval"
    ],
    image: "/images/how-step-4.png",
    accent: "#10B981"
  },
  {
    icon: RefreshCw,
    title: "Premium Printing",
    description: "Your approved design goes to production using DTG printing on premium quality fabric with vibrant, long-lasting colours.",
    tips: [
      "DTG (Direct-to-Garment) printing",
      "Premium quality fabric",
      "Vibrant, long-lasting colors",
      "Quality checked before packing"
    ],
    image: "/images/how-step-6.png",
    accent: "#F59E0B"
  },
  {
    icon: Truck,
    title: "Delivered to Your Door",
    description: "Your custom print is carefully packed and shipped pan-India. T-shirts in 5–7 days, hoodies in 7–10 business days.",
    tips: [
      "Carefully packed to prevent damage",
      "Pan-India delivery available",
      "T-shirts: 5-7 business days",
      "Hoodies: 7-10 business days"
    ],
    image: "/images/how-step-5.png",
    accent: "#EC4899"
  }
];

const HowItWorksPage = () => {
  return (
    <Layout>

      {/* ── Hero ── */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--lavender))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
              Simple Process
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              How It Works
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Getting your custom anime-style print is super easy. Follow our simple
              6-step process and have your unique creation delivered to your doorstep.
            </p>
          </div>
        </div>
      </section>

      {/* ── Quick Overview Pills ── */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS_STEPS.map((step) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={step.id} className="flex items-center gap-4 p-4 rounded-2xl bg-[hsl(var(--cream))]/50">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(350,70%,65%)]/10 to-[hsl(20,90%,70%)]/10 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-[hsl(350,70%,60%)]" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-[hsl(350,70%,55%)] uppercase tracking-wide">Step {step.id}</span>
                    <h3 className="font-semibold text-gray-900">{step.title}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FULL IMAGE VERTICAL STEP CARDS ── */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Step by Step Process
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Every step crafted to make your experience seamless and delightful
            </p>
          </div>

          <div className="space-y-6">
            {detailedSteps.map((step, index) => (
              <div
                key={index}
                className="group relative w-full rounded-3xl overflow-hidden cursor-pointer"
                style={{ height: '420px' }}
              >
                {/* Full Background Image */}
                <img
                  src={step.image}
                  alt={step.title}
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />

                {/* Base dark gradient — always visible at bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 transition-all duration-500 group-hover:from-black/95 group-hover:via-black/55 group-hover:to-black/20" />

                {/* Step number badge — top left */}
                <div className="absolute top-6 left-6 z-10">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-2 border-white/30"
                    style={{ backgroundColor: step.accent }}
                  >
                    <span className="text-white font-bold text-xl">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>

                {/* Icon badge — top right */}
                <div className="absolute top-6 right-6 z-10">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center border border-white/30 backdrop-blur-sm"
                    style={{ backgroundColor: step.accent + '40' }}
                  >
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Bottom content — always visible */}
                <div className="absolute bottom-0 left-0 right-0 z-10 p-8">

                  {/* Step label */}
                  <span
                    className="text-xs font-bold uppercase tracking-widest mb-2 block"
                    style={{ color: step.accent }}
                  >
                    Step {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Title — always visible */}
                  <h3 className="text-2xl font-bold text-white mb-2 leading-tight">
                    {step.title}
                  </h3>

                  {/* Description — always visible */}
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {step.description}
                  </p>

                  {/* Tips — slides up on hover */}
                  <div className="overflow-hidden max-h-0 group-hover:max-h-56 transition-all duration-500 ease-in-out">
                    <ul className="space-y-2 pt-4 mt-2 border-t border-white/20">
                      {step.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-center gap-3 text-sm text-gray-200">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: step.accent }}
                          >
                            <CheckCircle className="w-3 h-3 text-white" />
                          </div>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Hover hint — hides when hovered */}
                  <div className="flex items-center gap-2 mt-4 group-hover:opacity-0 transition-opacity duration-300">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-white/50">Hover to see details</span>
                  </div>

                </div>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Expected Timeline</h2>
            <p className="text-gray-600">Here's what to expect after you place your order</p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] -translate-x-1/2 hidden md:block" />
            <div className="space-y-8">
              {[
                { time: 'Day 1',    title: 'Photo Received',     description: 'We receive and review your photo' },
                { time: 'Day 1-2',  title: 'AI Artwork Created', description: 'Your photo is transformed into anime art' },
                { time: 'Day 2-3',  title: 'Preview Shared',     description: 'Review and approve your design' },
                { time: 'Day 3-5',  title: 'Printing & QC',      description: 'Your design is printed and quality checked' },
                { time: 'Day 5-10', title: 'Delivery',           description: 'Package shipped and delivered to you' }
              ].map((item, index) => (
                <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card className="border-0 shadow-soft inline-block">
                      <CardContent className="p-6">
                        <span className="text-sm font-semibold text-[hsl(350,70%,55%)]">{item.time}</span>
                        <h4 className="font-semibold text-gray-900 mt-1">{item.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden md:flex w-4 h-4 rounded-full bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] ring-4 ring-white shadow-soft z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-amber-100 rounded-full text-sm font-medium text-amber-700 mb-4">
              <HelpCircle className="w-4 h-4 inline mr-1" />
              Got Questions?
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about our custom prints</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-gray-100 rounded-2xl px-6 shadow-soft data-[state=open]:shadow-soft-lg transition-all duration-300"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
            It's super easy! Just send us your photo and we'll handle the rest.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white px-8 shadow-lg">
                <Instagram className="w-5 h-5 mr-2" />
                DM on Instagram
              </Button>
            </a>
            <a href={BRAND.whatsapp}>
              <Button size="lg" className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-8 shadow-lg">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default HowItWorksPage;
